import {
  ApiProperty,
  IsBoolean,
  IsDefined,
  IsEmail,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  ValidateNested,
  Type,
  IsArray,
} from './decorators-nest';
import { CreateUtmDto } from './utm.interface';

export enum MembershipPeriod {
  MONTHLY = 'monthly',
  THREE_MONTHS = 'three_months',
  SIX_MONTHS = 'six_months',
}

export enum ProgramType {
  ESSENTIAL = 'essentiel',
  AGE = 'age',
  SOPK = 'sopk',
  COMPLET = 'complet',
  SHOP = 'shop',
}

export class ProductSelectionDto {
  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withEssentielCoaching: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withEssentielSupplements: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withEssentielSupplement: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withOmegaSupplement: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withCoachingOption: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withStructurantOption: boolean;
  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withProtecteurOption: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  productQuantities: { [productId: string]: number }; // Map product ID to quantities
}

/**
 * Represents a single item in a mixed cart.
 * Each item carries its own purchaseType so the backend
 * can resolve the correct Stripe price (one_time vs recurring).
 */
export class CartItemDto {
  @IsString()
  @ApiProperty({ description: 'Product ID (frontend key)', example: 'essentielSupplement' })
  id: string;

  @IsString()
  @ApiProperty({ description: 'Stripe product ID', example: 'prod_xxx' })
  stripeProductId: string;

  @IsNumber()
  @ApiProperty({ description: 'Quantity', example: 3 })
  quantity: number;

  @IsEnum(['one_time', 'subscription'])
  @ApiProperty({
    description: 'Purchase type for this item',
    enum: ['one_time', 'subscription'],
    example: 'one_time',
  })
  purchaseType: 'one_time' | 'subscription';

  @IsNumber()
  @ApiProperty({ description: 'Resolved unit price (for display/verification)', example: 15.5 })
  unitPrice: number;
}

/**
 * ChatGPT Ads — identifiants de clic capturés par le navigateur.
 *
 * Stripe n'a jamais vu le clic sur l'annonce : ces deux valeurs sont le seul
 * lien entre la publicité et la commande. Elles sont recopiées dans les
 * `metadata` de la session Stripe, que le webhook relit pour alimenter l'API
 * Conversions d'OpenAI.
 *
 * ⚠️ Les deux champs sont `@IsOptional()` : le front envoie `null` quand le
 * cookie est absent (pas de clic sur une annonce, ou consentement `marketing`
 * refusé), et `@IsOptional()` est ce qui laisse passer `null` sans faire
 * échouer la création du Checkout.
 */
export class AttributionDto {
  @IsOptional()
  @IsString()
  @ApiProperty({
    description: "Identifiant de clic OpenAI, lu dans l'URL d'arrivée puis persisté en cookie first-party",
    required: false,
  })
  oppref?: string;

  @IsOptional()
  @IsString()
  @ApiProperty({
    description: 'Identifiant navigateur posé par le SDK OpenAI (cookie `__obref`)',
    required: false,
  })
  obref?: string;

  @IsOptional()
  @IsString()
  @ApiProperty({
    description: "Identifiant de clic TikTok, lu dans l'URL d'arrivée puis conservé pour la session",
    required: false,
  })
  ttclid?: string;

  @IsOptional()
  @IsString()
  @ApiProperty({
    description: 'Identifiant navigateur posé par le pixel TikTok (cookie `_ttp`)',
    required: false,
  })
  ttp?: string;
}

export class CreateStripeCustomerAndCheckoutSessioDto {
  @IsEmail()
  @ApiProperty({ description: 'Email du patient', example: 'patient@email.com' })
  email: string;

  @IsString()
  @ApiProperty({ description: 'Prénom', example: 'Jane' })
  firstname: string;

  @IsString()
  @ApiProperty({ description: 'Nom de famille', example: 'Doe' })
  lastname: string;

  @IsString()
  @ApiProperty({ description: 'Adresse du patient - ligne 1' })
  addressLine1: string;

  @IsString()
  @ApiProperty({ description: 'Adresse du patient - ligne 2', required: false })
  addressLine2: string;

  @IsString()
  @ApiProperty({ description: 'Code postal du patient' })
  zipCode: string;

  @IsString()
  @ApiProperty({ description: 'Ville du patient' })
  city: string;

  @IsString()
  @ApiProperty({ description: 'Pays du patient' })
  country: string;

  @IsString()
  @ApiProperty({ description: 'Numéro de téléphone du patient' })
  phoneNumber: string;

  @IsString()
  @IsOptional()
  @ApiProperty({ description: 'Source du patient', example: 'Search Engine' })
  source?: string;

  @ValidateNested()
  @Type(() => ProductSelectionDto)
  @IsDefined()
  @ApiProperty({
    description: 'Legacy product selection with quantities and options',
    example: {
      withOmegaSupplement: true,
      withEssentialSupplement: true,
      productQuantities: {
        omegaSupplement: 1,
        essentialSupplement: 2,
      },
    },
  })
  productSelection: ProductSelectionDto;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CartItemDto)
  @ApiProperty({
    description:
      'Detailed cart items with individual purchase types. When provided, takes priority over productSelection for building Stripe line_items.',
    required: false,
    type: [CartItemDto],
    example: [
      {
        id: 'essentielSupplement',
        stripeProductId: 'prod_xxx',
        quantity: 3,
        purchaseType: 'one_time',
        unitPrice: 15.5,
      },
      {
        id: 'essentielSupplements',
        stripeProductId: 'prod_yyy',
        quantity: 1,
        purchaseType: 'subscription',
        unitPrice: 31.5,
      },
    ],
  })
  cartItems?: CartItemDto[];

  @IsOptional()
  @IsBoolean()
  @ApiProperty({
    description: 'True when the cart contains both one_time and subscription items',
    required: false,
    example: true,
  })
  hasMixedCart?: boolean;

  @IsBoolean()
  @ApiProperty({ description: 'Indique si la livraison est activée' })
  withShipping: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: MembershipPeriod })
  @IsEnum(MembershipPeriod)
  membershipPeriod: MembershipPeriod;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: ProgramType })
  @IsEnum(ProgramType)
  programType: ProgramType;

  @IsString()
  @ApiProperty()
  domainURL: string;

  @IsOptional()
  @IsString()
  @ApiProperty({
    description: "Page d'origine du checkout (ex: 'sopk-s-plus'), propagée dans le success_url Stripe",
    required: false,
    example: 'sopk-s-plus',
  })
  checkoutOrigin?: string;

  @IsOptional()
  @IsString()
  @ApiProperty({ description: 'Code promotionnel utilisé', required: false })
  promotionCodeId?: string;

  @ValidateNested()
  @Type(() => CreateUtmDto)
  @IsOptional()
  @ApiProperty({ description: 'Paramètres UTM pour le suivi marketing' })
  utm?: CreateUtmDto;

  @ApiProperty({
    description: 'Enable/Disable pickup point',
    required: false,
  })
  @IsOptional()
  @IsBoolean()
  isPickup?: boolean;

  @IsOptional()
  @IsString()
  @ApiProperty({ description: 'Identifiant du point relais', required: false })
  pickupId?: string;

  @IsOptional()
  @IsString()
  @ApiProperty({ description: 'Adresse du point relais', required: false })
  pickupAddress?: string;

  @ValidateNested()
  @Type(() => AttributionDto)
  @IsOptional()
  @ApiProperty({
    description: "Identifiants d'attribution ChatGPT Ads et TikTok, recopiés dans les metadata de la session Stripe",
    required: false,
    type: AttributionDto,
  })
  attribution?: AttributionDto;
}

export class CreateCheckoutSessionDto extends CreateStripeCustomerAndCheckoutSessioDto {}

export class CreateStripeCheckoutSessionDto {
  @ValidateNested()
  @Type(() => ProductSelectionDto)
  @ApiProperty()
  productSelection: ProductSelectionDto;

  @ApiProperty({
    description: 'Enable/Disable pickup point',
    required: false,
  })
  @IsOptional()
  @IsBoolean()
  isPickup?: boolean;

  @IsNotEmpty()
  @IsString()
  @ApiProperty()
  domainURL: string;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty()
  withShipping: boolean;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ description: 'Type of the program', enum: ProgramType })
  @IsEnum(ProgramType)
  programType: ProgramType;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: MembershipPeriod })
  @IsEnum(MembershipPeriod)
  membershipPeriod: MembershipPeriod;
}

export class CheckoutSession {
  @IsString()
  @ApiProperty({ description: 'URL de la session de checkout' })
  url: string;
}

export class DeleteCustomerRequestDto {
  @IsEmail()
  @ApiProperty({ description: 'Email du client à supprimer', example: 'client@email.com' })
  email: string;
}

export class StripeWebhookDto {
  @IsNotEmpty()
  @IsString()
  @ApiProperty()
  id: string;

  @IsNotEmpty()
  @IsString()
  @ApiProperty()
  object: string;

  @IsNotEmpty()
  @IsString()
  @ApiProperty()
  api_version: string;

  @IsNumber()
  @ApiProperty()
  created: number;

  @IsDefined()
  @ApiProperty()
  data: object;

  @IsDefined()
  @ApiProperty()
  livemode: boolean;

  @IsNumber()
  @ApiProperty()
  pending_webhooks: number;

  @IsDefined()
  @ApiProperty()
  request: object;

  @IsString()
  @ApiProperty()
  type: string;
}

export class CreateSubscriptionRequestDto {
  @IsBoolean()
  @ApiProperty()
  withShipping: boolean;

  @ValidateNested()
  @Type(() => ProductSelectionDto)
  @ApiProperty()
  productSelection: ProductSelectionDto;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: MembershipPeriod })
  @IsEnum(MembershipPeriod)
  membershipPeriod: MembershipPeriod;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: ProgramType })
  @IsEnum(ProgramType)
  programType: ProgramType;
}

export class UpdateSubscriptionRequestDto {
  @IsBoolean()
  @ApiProperty()
  isPickup: boolean;

  @IsBoolean()
  @ApiProperty()
  withShipping: boolean;

  @ValidateNested()
  @Type(() => ProductSelectionDto)
  @ApiProperty()
  productSelection: ProductSelectionDto;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: MembershipPeriod })
  @IsEnum(MembershipPeriod)
  membershipPeriod: MembershipPeriod;

  @IsNotEmpty()
  @IsDefined()
  @ApiProperty({ enum: ProgramType })
  @IsEnum(ProgramType)
  programType: ProgramType;
}

export class UpdateShippingFeesDto {
  @IsBoolean()
  @ApiProperty()
  isPickup: boolean;
}

/** Stripe Customer Portal Session */
export interface Session {
  url: string;
}
